import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { Separator } from "@shared/components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import {
  ArrowLeft,
  FileText,
  Download,
  Receipt,
  Package,
  User,
  MapPin,
  Phone,
  Store,
  Calendar,
  DollarSign,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Copy,
  Check,
  AlertCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useReturnVoucherQuery } from "../hooks/useReturnVoucher";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import ReturnVoucherAction from "../components/ReturnVoucherAction";
import ReturnVoucherInvoiceCheck from "../components/ReturnVoucherInvoiceCheck";
import { toast } from "sonner";

const statusIcons = {
  pending: <Clock className="size-5 text-yellow-500" />,
  paid: <CheckCircle2 className="size-5 text-green-500" />,
  canceled: <AlertTriangle className="size-5 text-red-500" />,
  approved: <CheckCircle2 className="size-5 text-green-500" />,
  rejected: <AlertCircle className="size-5 text-red-500" />,
};

const statusColors = {
  pending:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100",
  paid: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
  canceled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
  approved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
};

const ReturnVoucherDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const { data: vouchers, isLoading, error } = useReturnVoucherQuery();

  const voucher = vouchers?.find((v) => v._id === id);

  const copyVoucherId = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      toast.success(t("returnVoucher.voucherIdCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("returnVoucher.failedToCopyVoucherId"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <ThreeDotLoader />
          <p className="text-sm text-muted-foreground">{t("main.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardContent className="text-center p-6">
            <div className="text-red-500 mb-4">
              <Package className="size-12 mx-auto" />
            </div>
            <h3 className="font-semibold text-lg mb-2">{t("common.error")}</h3>
            <p className="text-muted-foreground">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!voucher) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardContent className="text-center p-6">
            <div className="text-muted-foreground mb-4">
              <Receipt className="size-12 mx-auto" />
            </div>
            <h3 className="font-semibold text-lg mb-2">
              {t("returnVoucher.notFound")}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t("returnVoucher.voucherNotExists")}
            </p>
            <Button onClick={() => navigate(-1)}>
              <ArrowLeft className="size-4 mr-2" />
              {t("common.goBack")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">
              {t("returnVoucher.details")}
            </h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="font-mono">#{id.slice(-8)}</span>
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                onClick={copyVoucherId}
              >
                {copied ? (
                  <Check className="size-3 text-green-500" />
                ) : (
                  <Copy className="size-3" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Badge
            variant="outline"
            className={`${
              statusColors[voucher.status]
            } font-medium px-3 py-1.5 text-sm`}
          >
            {statusIcons[voucher.status]}
            <span className="ml-2">{t(`status.${voucher.status}`)}</span>
          </Badge>
          <ReturnVoucherAction
            returnVoucherId={voucher._id}
            voucher={voucher}
            showPDFActions={true}
            showUpdateStatus={false}
          />
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">{t("common.overview")}</TabsTrigger>
          <TabsTrigger value="invoice-check">
            {t("invoices.invoiceCheck")}
          </TabsTrigger>
          <TabsTrigger value="packages">{t("packages.packages")}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="size-5" />
                  {t("client.information")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Store className="size-5 text-blue-600" />
                  <div>
                    <div className="font-medium">
                      {voucher.client.brandName}
                    </div>
                    <div className="text-sm text-gray-600">
                      {voucher.client.name}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Phone className="size-5 text-green-600" />
                  <div>
                    <div className="font-medium">{voucher.client.phone}</div>
                    <div className="text-sm text-gray-600">
                      {t("client.phone")}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <MapPin className="size-5 text-red-600" />
                  <div>
                    <div className="font-medium">{voucher.client.address}</div>
                    <div className="text-sm text-gray-600">
                      {voucher.client.city}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Financial Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="size-5" />
                  {t("common.financialSummary")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="text-sm text-gray-600">
                      {t("common.totalCost")}
                    </span>
                    <span className="font-bold text-gray-700">
                      {voucher.totalCost} MAD
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                    <span className="text-sm text-blue-600">
                      {t("common.totalAmount")}
                    </span>
                    <span className="font-bold text-blue-700">
                      {voucher.totalAmount} MAD
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                    <span className="text-sm text-green-600">
                      {t("common.netAmount")}
                    </span>
                    <span className="font-bold text-green-700">
                      {voucher.totalNet} MAD
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Voucher Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="size-5" />
                {t("returnVoucher.information")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">
                    {t("common.created")}
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="size-4 text-gray-400" />
                    {format(voucher.createdAt, "dd/MM/yyyy HH:mm")}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">
                    {t("packages.count")}
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="size-4 text-gray-400" />
                    {voucher.packages.length} {t("packages.packages")}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">
                    {t("common.createdBy")}
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="size-4 text-gray-400" />
                    {voucher.user.brandName}
                  </div>
                </div>
              </div>

              {voucher.note && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">
                    {t("common.note")}
                  </label>
                  <div className="p-3 bg-gray-50 rounded text-sm">
                    {voucher.note}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoice Check Tab */}
        <TabsContent value="invoice-check">
          <ReturnVoucherInvoiceCheck voucher={voucher} />
        </TabsContent>

        {/* Packages Tab */}
        <TabsContent value="packages" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="size-5" />
                {t("packages.packageList")} ({voucher.packages.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {voucher.packages.map((pkg, index) => (
                  <div
                    key={pkg._id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          #{index + 1}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {pkg.trackingNumber || `PKG-${pkg._id.slice(-6)}`}
                        </Badge>
                        <Badge
                          variant={
                            pkg.invoiced || pkg.invoice
                              ? "default"
                              : "destructive"
                          }
                          className="text-xs"
                        >
                          {pkg.invoiced || pkg.invoice
                            ? t("invoices.invoiced")
                            : t("invoices.pending")}
                        </Badge>
                      </div>

                      <div className="text-sm text-gray-600">
                        <div>
                          {pkg.customer.name} • {pkg.customer.phone}
                        </div>
                        <div>
                          {pkg.customer.city} • {pkg.totalAmount} MAD
                        </div>
                      </div>
                    </div>

                    {(pkg.invoiced || pkg.invoice) && (
                      <Button variant="ghost" size="sm">
                        <Receipt className="size-4 mr-1" />
                        {t("invoices.viewInvoice")}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReturnVoucherDetails;
